import { useMemo, useRef, useState } from "react";
import { AutoSizer, List } from "react-virtualized";
import "react-virtualized/styles.css";
import Sheet from "./components/sheet";
import ExcelImporter from "./components/ExcelImporter";

export default function ShelfLabelApp() {
  const [product, setProduct] = useState("Apple");
  const [price, setPrice] = useState("10");
  const [currency, setCurrency] = useState("Egp");
  const [qty, setQty] = useState(24);
  const [products, setProducts] = useState([]); // multiple products

  // Label sizing (mm)
  const [labelW, setLabelW] = useState(65);
  const [labelH, setLabelH] = useState(35);
  const [gap, setGap] = useState(2);
  const [margin, setMargin] = useState(8);

  // Typography
  const [nameSize, setNameSize] = useState(14);
  const [priceSize, setPriceSize] = useState(24);
  const [showBorder, setShowBorder] = useState(true);
  const [uppercase, setUppercase] = useState(true);
  const [boldName, setBoldName] = useState(true);
  const [boldPrice, setBoldPrice] = useState(true);
  const [showSKU, setShowSKU] = useState(false);
  const [sku, setSKU] = useState("");

  const sheetRef = useRef(null);

  const labels = useMemo(
    () => Array.from({ length: Math.max(0, Number(qty) || 0) }),
    [qty]
  );

  const formattedPrice = useMemo(() => {
    const num = Number(price);
    if (Number.isNaN(num)) return `${currency}${price}`;
    return `${currency} ${num.toFixed(2)}`;
  }, [price, currency]);

  const handlePrint = () => {
    window.print();
  };

  const allLabels = useMemo(() => {
    if (products.length === 0) return [];
    // Flatten products into individual labels
    return products.flatMap((p) =>
      Array.from({ length: Number(p.Qty) || 1 }, () => p)
    );
  }, [products]);

  // rowRenderer for react-virtualized
  const rowRenderer = ({ index, key, style }) => {
    const p = allLabels[index];
    return (
      <div key={key} style={style}>
        <Sheet
          product={p.Product || p.Name || "Unnamed"}
          formattedPrice={`${p.Currency || "EGP"} ${Number(p.Price).toFixed(2)}`}
          qty={1} // Always 1 label per row
          options={{
            labelW,
            labelH,
            gap,
            margin,
            nameSize,
            priceSize,
            showBorder,
            uppercase,
            boldName,
            boldPrice,
            showSKU,
            sku: p.SKU || "",
          }}
        />
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-neutral-100 text-neutral-900">
      <header className="flex items-center justify-center gap-3 mb-6 border-b pb-3">
        <img
          src="/Zmart.jpg"
          alt="Zmart Logo"
          className="h-20 w-20 object-contain shadow-lg rounded-full ml-4 mt-4"
        />
        <h1 className="text-2xl font-bold items-center mt-5">
          Zmart Supermarket
        </h1>
      </header>

      {/* Controls (hidden in print) */}
      <div className="print:hidden max-w-6xl mx-auto p-4 md:p-6">
        <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight">
          Supermarket Shelf Label Maker
        </h1>
        <p className="text-sm text-neutral-600 mt-1">
          Enter product details and print A4 sheets of labels to cut.
        </p>

        <div className="grid md:grid-cols-3 gap-4 mt-6">
          {/* Product Form */}
          <div className="bg-white rounded-2xl shadow p-4 space-y-3">
            <h2 className="font-semibold text-lg">Product</h2>
            <label className="block text-sm">Name</label>
            <input
              value={product}
              onChange={(e) => setProduct(e.target.value)}
              className="w-full border rounded-xl px-3 py-2 focus:outline-none focus:ring"
              placeholder="Product name"
            />

            <div className="grid grid-cols-5 gap-2">
              <div className="col-span-1">
                <label className="block text-sm">Curr.</label>
                <input
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  className="w-full border rounded-xl px-3 py-2"
                  maxLength={3}
                />
              </div>
              <div className="col-span-4">
                <label className="block text-sm">Price</label>
                <input
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  className="w-full border rounded-xl px-3 py-2"
                  placeholder="e.g. 12.50"
                  inputMode="decimal"
                />
              </div>
            </div>

            <label className="block text-sm">Quantity of labels</label>
            <input
              value={qty}
              onChange={(e) => setQty(Number(e.target.value))}
              className="w-full border rounded-xl px-3 py-2"
              type="number"
              min={0}
            />

            <div className="flex items-center gap-3 pt-2">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={uppercase} onChange={(e) => setUppercase(e.target.checked)} />
                Uppercase name
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={boldName} onChange={(e) => setBoldName(e.target.checked)} />
                Bold name
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={boldPrice} onChange={(e) => setBoldPrice(e.target.checked)} />
                Bold price
              </label>
            </div>

            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={showSKU} onChange={(e) => setShowSKU(e.target.checked)} />
                Show SKU/code
              </label>
              {showSKU && (
                <input
                  value={sku}
                  onChange={(e) => setSKU(e.target.value)}
                  className="flex-1 border rounded-xl px-3 py-2 text-sm"
                  placeholder="SKU / Code"
                />
              )}
            </div>
          </div>

          {/* Excel Import */}
          <ExcelImporter
            onDataImported={setProducts}
            onRemoveFile={() => setProducts([])}
          />

          {/* Label Size Settings */}
          <div className="bg-white rounded-2xl shadow p-4 space-y-3">
            <h2 className="font-semibold text-lg">Label Size (mm)</h2>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-sm">Width</label>
                <input
                  type="number"
                  value={labelW}
                  min={20}
                  max={200}
                  onChange={(e) => setLabelW(Number(e.target.value))}
                  className="w-full border rounded-xl px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm">Height</label>
                <input
                  type="number"
                  value={labelH}
                  min={10}
                  max={200}
                  onChange={(e) => setLabelH(Number(e.target.value))}
                  className="w-full border rounded-xl px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm">Gap</label>
                <input
                  type="number"
                  value={gap}
                  min={0}
                  max={10}
                  onChange={(e) => setGap(Number(e.target.value))}
                  className="w-full border rounded-xl px-3 py-2"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm">Page margin (mm)</label>
              <input
                type="number"
                value={margin}
                min={0}
                max={20}
                onChange={(e) => setMargin(Number(e.target.value))}
                className="w-full border rounded-xl px-3 py-2"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm">Name font (px)</label>
                <input
                  type="number"
                  value={nameSize}
                  min={8}
                  max={72}
                  onChange={(e) => setNameSize(Number(e.target.value))}
                  className="w-full border rounded-xl px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm">Price font (px)</label>
                <input
                  type="number"
                  value={priceSize}
                  min={10}
                  max={120}
                  onChange={(e) => setPriceSize(Number(e.target.value))}
                  className="w-full border rounded-xl px-3 py-2"
                />
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm pt-1">
              <input type="checkbox" checked={showBorder} onChange={(e) => setShowBorder(e.target.checked)} />
              Show label border (helps cutting)
            </label>
          </div>
        </div>

        {/* Actions */}
        <div className="bg-white rounded-2xl shadow p-4 flex flex-col justify-between mt-6">
          <div>
            <h2 className="font-semibold text-lg">Actions</h2>
            <p className="text-sm text-neutral-600">
              Use your browser’s Print dialog to save as PDF or print to your A4
              printer.
            </p>
          </div>
          <button
            onClick={handlePrint}
            className="mt-4 inline-flex items-center justify-center gap-2 text-white bg-black rounded-2xl px-4 py-3 hover:opacity-90 active:opacity-80"
          >
            <span>Print / Save PDF</span>
          </button>
          <div className="text-xs text-neutral-500 mt-3">
            Tip: For 100% real size, set scale to 100% in print options and
            margins to none (handled below).
          </div>
        </div>

        {/* Preview */}
        <div className="mt-6">
          <h3 className="font-semibold mb-2">Preview (A4)</h3>
          <div className="overflow-auto rounded-2xl border bg-white" style={{ height: 600 }}>
            {products.length > 0 ? (
              <AutoSizer>
                {({ height, width }) => (
                  <List
                    width={width}
                    height={height}
                    rowCount={allLabels.length}
                    rowHeight={120} // adjust depending on your Sheet height
                    rowRenderer={rowRenderer}
                  />
                )}
              </AutoSizer>
            ) : (
              <Sheet
                ref={sheetRef}
                product={product}
                formattedPrice={formattedPrice}
                qty={qty}
                options={{
                  labelW,
                  labelH,
                  gap,
                  margin,
                  nameSize,
                  priceSize,
                  showBorder,
                  uppercase,
                  boldName,
                  boldPrice,
                  showSKU,
                  sku,
                }}
              />
            )}
          </div>
        </div>
      </div>

      {/* Print-only sheet */}
      <div className="hidden print:block">
        {products.length > 0 ? (
          <Sheet
            ref={sheetRef}
            product={null}
            formattedPrice={null}
            qty={allLabels.length}
            options={{
              labelW,
              labelH,
              gap,
              margin,
              nameSize,
              priceSize,
              showBorder,
              uppercase,
              boldName,
              boldPrice,
              showSKU,
              sku,
              // Pass allLabels for Sheet to render all imported products
              allLabels,
            }}
          />
        ) : (
          <Sheet
            ref={sheetRef}
            product={product}
            formattedPrice={formattedPrice}
            qty={qty}
            options={{
              labelW,
              labelH,
              gap,
              margin,
              nameSize,
              priceSize,
              showBorder,
              uppercase,
              boldName,
              boldPrice,
              showSKU,
              sku,
            }}
          />
        )}
      </div>

      <style>{`
        @page { size: A4 portrait; margin: 0; }
        @media print {
          body { background: white; }
        }
      `}</style>
    </div>
  );
}
